import type {
  BulkAssignRoomOperatorsRequest,
  CreatePointsSystemRequest,
  CreateRoomRequest,
  CreateTemplateRequest,
  PatchPlatformSettingsRequest,
  PutActivityOverrideRequest,
  PutActivityRetentionRequest,
  PatchRoomRequest,
  PutTemplateRequest,
  Room,
} from '@brush/api-client';
import { fetchAllPages, MAX_PAGE_SIZE, MAX_PAGES_READ, retryUnlessClientError } from '@brush/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './api';

/**
 * Which build the API is running, for the About panel. Enabled only while the panel is open — it
 * is the one place the answer is shown, and an app that asked on every load would spend a request
 * per session on a screen most people never open.
 *
 * `staleTime: Infinity`: a running API does not change build under a page. A reload is what picks
 * up a new one, and a reload refetches anyway.
 */
export function useApiVersion(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['api-version'],
    queryFn: () => api.version(),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

const keys = {
  rooms: ['rooms'] as const,
  players: ['players'] as const,
  tournaments: ['tournaments'] as const,
  ratingBoards: ['rating-boards'] as const,
  platformTags: ['platform-tags'] as const,
  platformTagUsage: ['platform-tags', 'usage'] as const,
  platformSettings: ['platform-settings'] as const,
  roomOperators: ['room-operators'] as const,
  directorySearch: ['directory', 'users'] as const,
  templates: ['templates'] as const,
  leaderboards: ['leaderboards'] as const,
  pointsSystems: ['points-systems'] as const,
  activityOverview: ['activity', 'overview'] as const,
  activitySettings: ['activity', 'settings'] as const,
  erasures: ['activity', 'erasures'] as const,
};

// A room PATCH must carry every field; null means "leave unchanged".
const EMPTY_ROOM_PATCH: PatchRoomRequest = {
  name: null,
  location: null,
  status: null,
  primaryColor: null,
  accentColor: null,
  logo: null,
  timeZone: null,
  gamingDayStartMinutes: null,
  isInternal: null,
  canLeaderboard: null,
  canFantasy: null,
  canRatings: null,
  defaultTags: null,
};

/** Who the caller is. Gates the whole app: admin-web is for platform administrators only. */
export function useCurrentUser() {
  const api = useApi();
  return useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 5 * 60_000 });
}

export function useRooms() {
  const api = useApi();
  return useQuery({ queryKey: keys.rooms, queryFn: () => api.rooms.list() });
}

/**
 * How many players the platform holds — a number, not a list.
 *
 * The overview used to count the rows of `GET /api/players`, which returned every player at every
 * room to render one figure. That endpoint is gone (ADR 0010's read tier), and it should not have
 * been the source for this even while it existed: a count is what the screen asks for, and the
 * whole table was what it had to read to get it.
 */
export function usePlayerCount() {
  const api = useApi();
  return useQuery({ queryKey: [...keys.players, 'count'], queryFn: () => api.players.count() });
}

/**
 * How many tournaments the platform holds — the server's count of the whole set. The Overview used to
 * take `useTournaments().length`, which reads a list capped at the maximum page size and so reported
 * 200 for any platform holding more (known gap 6).
 */
export function useTournamentCount() {
  const api = useApi();
  return useQuery({ queryKey: [...keys.tournaments, 'count'], queryFn: () => api.tournaments.count() });
}

/** The admin Overview's activity cards (ADR 0012 Decision 16). */
export function useAdminOverview() {
  const api = useApi();
  return useQuery({ queryKey: keys.activityOverview, queryFn: () => api.activity.overview() });
}

/**
 * One room's events for the points designer's test scope (§9.8): the room's whole list, read a page at a time
 * without results (known gap 108), in place of its newest 200 tournaments with every result. The pickers reach
 * outside the test scope's dates, so nothing narrows the read. `complete` is false when it ended short.
 */
export function useRoomEvents(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.tournaments, roomId, 'events'],
    queryFn: ({ signal }) =>
      fetchAllPages(
        (page, pageSize) =>
          api.tournaments.listForRoom(roomId ?? '', { page, pageSize, sort: 'date', dir: 'asc' }),
        MAX_PAGE_SIZE,
        MAX_PAGES_READ,
        signal,
      ),
    enabled: !!roomId,
    staleTime: 60_000,
    retry: retryUnlessClientError,
  });
}

/**
 * One room's tag vocabulary with how many tournaments carry each tag (`GET /api/rooms/{roomId}/tournament-tags`):
 * the test scope's tag chips, in place of a count over a capped tournament list (known gap 108).
 */
export function useRoomTagUsage(roomId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.tournaments, roomId, 'tags'],
    queryFn: () => api.tournaments.tagUsage(roomId ?? ''),
    enabled: !!roomId,
    staleTime: 5 * 60_000,
    retry: retryUnlessClientError,
  });
}

export function usePlatformTags() {
  const api = useApi();
  return useQuery({ queryKey: keys.platformTags, queryFn: () => api.platformTags.list() });
}

/**
 * How many tournaments carry each platform tag, every room counted, on the server (known gap 92). It replaced a
 * count over `listAll()`, the platform's newest 200 tournaments with every result, which read low past 200.
 */
export function usePlatformTagUsage() {
  const api = useApi();
  return useQuery({ queryKey: keys.platformTagUsage, queryFn: () => api.platformTags.usage() });
}

export function usePlatformSettings() {
  const api = useApi();
  return useQuery({ queryKey: keys.platformSettings, queryFn: () => api.platformSettings.get() });
}

export function useCreateRoom() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoomRequest) => api.rooms.create(body),
    onSuccess: (created) => {
      // THE LIST CARRIES IT IMMEDIATELY, because the dialog stays open on the room that now
      // exists and resolves itself against this list every render. Without it there is a window
      // between the create returning and the refetch landing in which the room is in neither —
      // and a dialog that resolves to nothing there reads as a room that has gone. Not an
      // optimistic update: this is the record the server just answered with, appended rather than
      // guessed, and the invalidation below still has the last word.
      qc.setQueryData<Room[]>(keys.rooms, (rooms) =>
        rooms && !rooms.some((r) => r.id === created.id) ? [...rooms, created] : rooms);

      void qc.invalidateQueries({ queryKey: keys.rooms });
      // Creating a room auto-creates its default rating BOARD server-side (locked rule 3, ADR
      // 0011 Decision 12). It was a rating group until Phase 4b retired that entity.
      void qc.invalidateQueries({ queryKey: keys.ratingBoards });
    },
  });
}

export function usePatchRoom() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<PatchRoomRequest> }) =>
      api.rooms.patch(id, { ...EMPTY_ROOM_PATCH, ...body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.rooms }),
  });
}

/**
 * Deleting a room takes its whole graph with it server-side — players, tournaments and their
 * results, leaderboards, rating boards and their snapshots, fantasy contests and entries, its
 * template content, and the operator assignments pointing at it. The invalidations mirror that,
 * because every one of those lists is now wrong.
 */
export function useDeleteRoom() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.rooms.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.rooms });
      void qc.invalidateQueries({ queryKey: keys.players });
      void qc.invalidateQueries({ queryKey: keys.tournaments });
      void qc.invalidateQueries({ queryKey: keys.ratingBoards });
      void qc.invalidateQueries({ queryKey: keys.roomOperators });
    },
  });
}

export function useAddPlatformTag() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tag: string) => api.platformTags.add(tag),
    onSuccess: (list) => {
      qc.setQueryData(keys.platformTags, list);
      // A tag just added may already be on tournaments, matched without regard to case.
      void qc.invalidateQueries({ queryKey: keys.platformTagUsage });
    },
  });
}

export function useRemovePlatformTag() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tag: string) => api.platformTags.remove(tag),
    onSuccess: (removal) => {
      qc.setQueryData(keys.platformTags, removal.data);
      // Removing a tag strips it from tournaments server-side.
      void qc.invalidateQueries({ queryKey: keys.tournaments });
      void qc.invalidateQueries({ queryKey: keys.platformTagUsage });
    },
  });
}

export function usePatchPlatformSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchPlatformSettingsRequest) => api.platformSettings.patch(body),
    onSuccess: (settings) => qc.setQueryData(keys.platformSettings, settings),
  });
}

// ---------- templates (the catalogue; rooms supply their own content elsewhere) ----------

export function useTemplates() {
  const api = useApi();
  return useQuery({ queryKey: keys.templates, queryFn: () => api.templates.list() });
}

export function useCreateTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateRequest) => api.templates.create(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.templates }),
  });
}

export function useUpdateTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PutTemplateRequest }) =>
      api.templates.update(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.templates }),
  });
}

export function useDeleteTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.templates.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.templates }),
  });
}

// ---------- points systems (the platform catalogue every room may clone from) ----------

/**
 * Every leaderboard on the platform, read for one reason: to count how many of them use each
 * points system (§9.0). Unpaged, like every list endpoint but tournaments.
 */
export function useAllLeaderboards() {
  const api = useApi();
  return useQuery({ queryKey: keys.leaderboards, queryFn: () => api.leaderboards.list() });
}

export function usePlatformPointsSystems(includeArchived: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.pointsSystems, { includeArchived }],
    queryFn: () => api.pointsSystems.listPlatform(includeArchived),
  });
}

/**
 * One version's definition. The system DTO carries only summaries — number, status, dates —
 * because a list of systems has no business shipping every stored definition with it.
 */
export function usePointsSystemVersion(systemId: string | null, versionNumber: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.pointsSystems, systemId, 'versions', versionNumber],
    queryFn: () => api.pointsSystems.version(systemId ?? '', versionNumber ?? 0),
    enabled: !!systemId && versionNumber !== null,
    // A published version is frozen, and a draft is only ever changed from this screen — which
    // invalidates on save. Nothing else moves it underneath us.
    staleTime: 5 * 60_000,
  });
}

/** Creates a *platform* system — owned by nobody's room, offered to all of them. */
export function useCreatePlatformPointsSystem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePointsSystemRequest) => api.pointsSystems.createPlatform(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

export function usePatchPointsSystem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, description }: { id: string; name?: string; description?: string }) =>
      api.pointsSystems.patch(id, { name: name ?? null, description: description ?? null }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

/**
 * A 409 here is not a failure to report as one — §2.4 says it means the system has a version some
 * leaderboard has pinned, and the answer is Archive. The caller surfaces the server's message,
 * which leads with exactly that.
 */
export function useDeletePointsSystem() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.pointsSystems.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

export function useSetPointsSystemArchived() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? api.pointsSystems.archive(id) : api.pointsSystems.unarchive(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

/** A new draft. `copyFromVersion` is §2.3's edit path for a published version. */
export function useCreatePointsSystemVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, copyFromVersion }: { id: string; copyFromVersion: number | null }) =>
      // No previewScope: §2.1 says a test scope is not carried across versions, and the
      // designer sets one on the new draft's first save.
      api.pointsSystems.createVersion(id, {
        copyFromVersion,
        definition: null,
        previewScope: null,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

export function useSavePointsSystemVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      n,
      definition,
      previewScope,
    }: {
      id: string;
      n: number;
      definition: unknown;
      /** §2.1's test scope, written to its own column. Null clears it. */
      previewScope: unknown;
    }) => api.pointsSystems.putVersion(id, n, { definition, previewScope }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

/** The freeze. After this the version may never change, and a leaderboard may pin it. */
export function usePublishPointsSystemVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => api.pointsSystems.publishVersion(id, n),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

export function useDeletePointsSystemVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, n }: { id: string; n: number }) => api.pointsSystems.removeVersion(id, n),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.pointsSystems }),
  });
}

/**
 * The designer's live board: a definition scored against a room's real results, saving nothing.
 *
 * Three settings carry the weight here. The key is the definition itself, so returning to a shape
 * already scored is free rather than another request. `staleTime: Infinity` follows from that —
 * the same definition over the same room gives the same answer, and the endpoint is the most
 * expensive thing the API does. And `retry: false`, because the one failure worth expecting is
 * the rate limit, and retrying into it is how a caller stays refused.
 */
export function usePointsPreview({
  definition,
  scope,
  roomId,
  enabled,
}: {
  definition: unknown;
  /** §7.3's scope, sent beside the definition rather than inside it (§3.2). */
  scope: unknown;
  roomId: string | null;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    // Deliberately outside the `points-systems` prefix: everything that writes a system
    // invalidates that whole tree, and a saved draft has not changed what any *previously scored*
    // definition produces. Keeping the board here means a save costs no extra preview request.
    //
    // Both documents are in the key, because both change the answer — a board cached against one
    // scope must not be shown for another.
    queryKey: ['points-preview', roomId, JSON.stringify(definition), JSON.stringify(scope)],
    queryFn: () =>
      api.pointsSystems.preview({
        definition,
        roomId: roomId ?? '',
        scope,
        eventIds: null,
        limit: 100,
        // No drill-down with the board: the working for one player is its own request, made when
        // a row is opened. See `usePointsPreviewBreakdown`.
        breakdownPlayerId: null,
      }),
    enabled: enabled && !!roomId,
    staleTime: Infinity,
    retry: false,
    // Keeps the last board on screen while the next one is worked out, rather than blanking a
    // 200-row table on every edit.
    placeholderData: (previous) => previous,
  });
}

/**
 * §7.4's working for one player of the previewed board, fetched when a standings row is opened.
 *
 * Keyed under the board's own key plus the player, so it is cached per player against the exact
 * definition and scope that produced the row — opening the same row twice costs no request, and an
 * edit to either document refetches the open panel rather than leaving working that explains a
 * total no longer on screen.
 *
 * `limit: 1` because the board is already rendered: what this call is for is the `breakdown` beside
 * it, and asking for a hundred standings rows a second time is the one part of the payload nobody
 * would read. It spends a permit from the preview endpoint's per-caller allowance, which is why it
 * fires on a click rather than travelling with every keystroke's board.
 */
export function usePointsPreviewBreakdown({
  definition,
  scope,
  roomId,
  playerId,
  enabled,
}: {
  definition: unknown;
  scope: unknown;
  roomId: string | null;
  /** Whose working to fetch, or null when no row is open. */
  playerId: string | null;
  enabled: boolean;
}) {
  const api = useApi();
  return useQuery({
    queryKey: [
      'points-preview',
      roomId,
      JSON.stringify(definition),
      JSON.stringify(scope),
      'breakdown',
      playerId,
    ],
    queryFn: () =>
      api.pointsSystems
        .preview({
          definition,
          roomId: roomId ?? '',
          scope,
          eventIds: null,
          limit: 1,
          breakdownPlayerId: playerId,
        })
        // Null is an answer — the player is not on this board — and it has to survive as one
        // rather than being turned into an empty object the panel would render as a scoreless
        // season.
        .then((result) => result.breakdown),
    enabled: enabled && !!roomId && !!playerId,
    staleTime: Infinity,
    retry: false,
  });
}

// ---------- room operators (platform administration) ----------

export function useRoomOperators() {
  const api = useApi();
  return useQuery({ queryKey: keys.roomOperators, queryFn: () => api.roomOperators.list() });
}

/**
 * Searches the workforce directory. Admin-only on the server.
 *
 * The caller passes an already-debounced term and decides when it is long enough — `enabled` is
 * what keeps a one-character search from spending a Graph round trip to be told it was too short.
 *
 * `staleTime: Infinity` because a directory does not change under a keystroke, and typing back to
 * a term already searched should cost nothing. `retry: false` because the failures here are a
 * refusal or a rate limit, and retrying into either is how a caller stays refused.
 */
export function useDirectorySearch(query: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...keys.directorySearch, query],
    queryFn: () => api.directory.searchUsers(query),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Links a staged set in one act. All-or-nothing on the server, so there is no partial success to
 * reconcile here — either the list is invalidated or nothing happened.
 */
export function useLinkRoomOperators() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkAssignRoomOperatorsRequest) => api.roomOperators.bulkAssign(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.roomOperators }),
  });
}

/** Renames one link. Scoped to that row, so another room's name for the same person is untouched. */
export function useRenameRoomOperator() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.roomOperators.rename(id, { name }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.roomOperators }),
  });
}

export function useRemoveRoomOperator() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.roomOperators.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.roomOperators }),
  });
}

// ---------- activity settings: retention and engagement-type overrides (ADR 0012 step 26) ----------

/** Retention and overrides, as stored. A null retention is NOT SET — never show it as a figure. */
/** Erasures whose sign-in an administrator still has to delete from the tenant (step 28's follow-up). */
export function usePendingSignInRemovals() {
  const api = useApi();
  return useQuery({ queryKey: keys.erasures, queryFn: () => api.activity.erasures() });
}

/**
 * Marks an erasure's sign-in removed. The queue and the Overview's count both change, so both are refetched —
 * the server's answer is the queue, not this mutation's guess at it.
 */
export function useMarkSignInRemoved() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (replacementId: string) => api.activity.markSignInRemoved(replacementId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.erasures });
      void qc.invalidateQueries({ queryKey: keys.activityOverview });
    },
  });
}

export function useActivitySettings() {
  const api = useApi();
  return useQuery({ queryKey: keys.activitySettings, queryFn: () => api.activity.settings() });
}

/**
 * What a proposed retention would delete on the next purge — the platform's, or with `roomId` one room's.
 * A mutation rather than a query because it is asked once, just before a shorter period is saved, and an
 * answer cached for a minute would describe a purge over data that has since grown.
 */
export function useRetentionImpact() {
  const api = useApi();
  return useMutation({
    mutationFn: ({ roomId, body }: { roomId: string | null; body: PutActivityRetentionRequest }) =>
      roomId ? api.activity.roomRetentionImpact(roomId, body) : api.activity.retentionImpact(body),
  });
}

export function usePutRetention() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // The two answers differ in shape and nothing reads either: the settings are refetched instead.
    mutationFn: async ({ roomId, body }: { roomId: string | null; body: PutActivityRetentionRequest }) => {
      if (roomId) await api.activity.putRoomRetention(roomId, body);
      else await api.activity.putRetention(body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.activitySettings }),
  });
}

export function usePutActivityOverride() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PutActivityOverrideRequest) => api.activity.putOverride(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.activitySettings }),
  });
}

export function useRemoveActivityOverride() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.activity.removeOverride(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.activitySettings }),
  });
}
